import { useState } from 'react';
import { useLeaderboard, type LeaderboardEntry } from '../services/tournamentService';
import { Trophy, Search, X, Loader2 } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import TCoinIcon from '../components/TCoinIcon';
import AvatarDisplay from '../components/AvatarDisplay';

const games = ['All', 'PUBG', 'Free Fire', 'Wild Rift', 'MLBB'];

export default function Leaderboard() {
    const [gameFilter, setGameFilter] = useState('All');
    const { entries: realLeaderboard } = useLeaderboard();

    // Search state
    const [searchInput, setSearchInput] = useState('');
    const [searchResult, setSearchResult] = useState<LeaderboardEntry | null>(null);
    const [searchError, setSearchError] = useState('');
    const [searching, setSearching] = useState(false);

    const leaderboard = realLeaderboard;
    const filtered = leaderboard.filter(p => gameFilter === 'All' || p.game === gameFilter);

    const handleSearch = async () => {
        const term = searchInput.trim();
        if (!term) return;
        setSearching(true);
        setSearchError('');
        setSearchResult(null);

        try {
            const usersRef = collection(db, 'users');
            // Try playerId first if 10-digit number, otherwise try username
            const isPlayerId = /^\d{10}$/.test(term);
            const q = query(usersRef, where(isPlayerId ? 'playerId' : 'username', '==', term));
            const snap = await getDocs(q);

            if (snap.empty) {
                // If searched by username and failed, also try playerId
                if (!isPlayerId) {
                    const q2 = query(usersRef, where('playerId', '==', term));
                    const snap2 = await getDocs(q2);
                    if (!snap2.empty) {
                        const d = snap2.docs[0].data();
                        setSearchResult({
                            rank: 0,
                            username: d.username || 'Unknown',
                            game: d.primaryGame || 'PUBG',
                            tournaments: d.tournamentsPlayed || 0,
                            winnings: d.totalWinnings || 0,
                            photoURL: d.photoURL || '',
                            playerId: d.playerId || '',
                        });
                        setSearching(false);
                        return;
                    }
                }
                setSearchError('No player found');
            } else {
                const d = snap.docs[0].data();
                setSearchResult({
                    rank: 0,
                    username: d.username || 'Unknown',
                    game: d.primaryGame || 'PUBG',
                    tournaments: d.tournamentsPlayed || 0,
                    winnings: d.totalWinnings || 0,
                    photoURL: d.photoURL || '',
                    playerId: d.playerId || '',
                });
            }
        } catch (err) {
            console.error('Leaderboard search error:', err);
            setSearchError('Search failed');
        }
        setSearching(false);
    };

    const clearSearch = () => {
        setSearchInput('');
        setSearchResult(null);
        setSearchError('');
    };

    return (
        <div className="page-wrapper" style={{ maxWidth: 800 }}>
            <h1 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.8rem', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 12 }} className="animate-in">
                <img src="/icons/podium.png" alt="Leaderboard" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                Leaderboard
            </h1>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24 }} className="animate-in">Top players by total winnings</p>

            {/* Search Bar */}
            <div className="animate-in" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            className="form-input"
                            placeholder="Search by Player ID or username..."
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                            style={{ paddingLeft: 38, width: '100%' }}
                        />
                        {searchInput && (
                            <button onClick={clearSearch} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}>
                                <X size={16} />
                            </button>
                        )}
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={handleSearch}
                        disabled={searching || !searchInput.trim()}
                        style={{ padding: '10px 18px', whiteSpace: 'nowrap' }}
                    >
                        {searching ? <Loader2 size={16} className="spin" /> : <Search size={16} />}
                    </button>
                </div>
            </div>

            {/* Search Result */}
            {searchResult && (
                <div className="card animate-in" style={{ marginBottom: 16, border: '1px solid var(--accent)', background: 'rgba(99,102,241,0.06)' }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 8, fontWeight: 600 }}>Search Result</div>
                    <div className="lb-row" style={{ background: 'transparent', padding: 0 }}>
                        <div className="lb-rank">🔍</div>
                        <AvatarDisplay avatar={searchResult.photoURL || null} username={searchResult.username} size={36} className="lb-avatar" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="lb-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{searchResult.username}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                {searchResult.playerId && <span style={{ fontFamily: 'monospace' }}>ID: {searchResult.playerId}</span>}
                                {searchResult.playerId && ' · '}{searchResult.game}
                            </div>
                        </div>
                        <div className="lb-stats">
                            <div>
                                <div className="lb-stat-val" style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                                    <Trophy size={13} style={{ color: 'var(--warning)' }} /> {searchResult.tournaments}
                                </div>
                                <div className="lb-stat-label">Played</div>
                            </div>
                            <div>
                                <div className="lb-stat-val" style={{ color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                                    <TCoinIcon size={12} /> {searchResult.winnings.toLocaleString()}
                                </div>
                                <div className="lb-stat-label">Winnings</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {searchError && (
                <div className="animate-in" style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.88rem', padding: '12px 0', marginBottom: 12 }}>
                    {searchError}
                </div>
            )}

            <div className="filter-bar animate-in">
                {games.map(g => (
                    <button key={g} className={`filter-chip ${gameFilter === g ? 'active' : ''}`} onClick={() => setGameFilter(g)}>{g}</button>
                ))}
            </div>

            <div className="card animate-in">
                {filtered.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        No players on the leaderboard yet.
                    </div>
                ) : filtered.map((p, i) => (
                    <div key={p.rank} className={`lb-row animate-in stagger-${(i % 6) + 1}`}>
                        <div className="lb-rank">
                            {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : p.rank}
                        </div>
                        <AvatarDisplay avatar={(p as LeaderboardEntry).photoURL || null} username={p.username} size={36} className="lb-avatar" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="lb-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.username}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.game}</div>
                        </div>
                        <div className="lb-stats">
                            <div>
                                <div className="lb-stat-val" style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                                    <Trophy size={13} style={{ color: 'var(--warning)' }} /> {p.tournaments}
                                </div>
                                <div className="lb-stat-label">Played</div>
                            </div>
                            <div>
                                <div className="lb-stat-val" style={{ color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                                    <TCoinIcon size={12} /> {p.winnings.toLocaleString()}
                                </div>
                                <div className="lb-stat-label">Winnings</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
